import React from 'react';
import { NavLink as RouterLink, useMatch, useResolvedPath } from 'react-router';

import styled from '@emotion/styled';

import FullRightTriangle from '../Icons/FullRightTriangle';

export const NavLink = styled(RouterLink)`
  text-decoration: none;
  color: black;
  font-weight: 500;
  display: flex;
  gap: 0.5rem;

  &.footer {
    font-weight: 600;
  }

  &.hamburger-menu {
    font-size: 1rem;
    font-weight: 600;
    color: black;
  }
`;

interface IExternalLink {
  children: React.ReactNode;
  to: string;
  className?: string;
}

export const ExternalLink: React.FC<IExternalLink> = ({
  children,
  to,
  className,
}) => {
  return (
    <AnchorTag
      href={to}
      target='_blank'
      rel='noopener noreferrer'
      className={className}
    >
      {children}
    </AnchorTag>
  );
};

export const AnchorTag = styled.a`
  text-decoration: none;
  font-weight: 500;

  &.footer {
    display: grid;
    grid-template-columns: 1.5rem auto;
    gap: 0.5rem;
    font-weight: 600;
  }

  &.header_nav__mobile {
    @media (max-width: 480px) {
      display: None;
    }
  }
`;

export const ButtonLink = styled(RouterLink)`
  text-decoration: none;
  font-weight: 500;
`;

interface INavItem {
  icon: React.ReactNode;
  children: string;
  path: string;
  className?: string;
  // What the page is for, shown as the link's tooltip.
  hint?: string;
  // A count shown at the end of the link (the Inbox).
  badge?: number;
}

export const NavBoxItem: React.FC<INavItem> = ({
  icon,
  path,
  children,
  className,
  hint,
  badge,
}) => {
  const resolved = useResolvedPath(path);
  const match = useMatch({ path: resolved.pathname, end: false });
  const isActive = match !== null;

  return (
    <NavigationBoxLink
      className={`${className ?? ''} ${isActive ? 'active' : ''}`.trim()}
      to={path}
      title={hint ? `${children}: ${hint}` : children}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className='nav-icon'>{icon}</span>
      <span className='nav-label'>{children}</span>
      {!!badge && <span className='nav-badge'>{badge > 99 ? '99+' : badge}</span>}
    </NavigationBoxLink>
  );
};

export const MobileNavBoxItem: React.FC<INavItem> = ({
  icon,
  path,
  children,
  className,
}) => {
  const resolved = useResolvedPath(path);
  const match = useMatch({ path: resolved.pathname, end: false });
  const isActive = match !== null;

  return (
    <MobileNavigationBoxLink
      className={`${className ?? ''} ${isActive ? 'active' : ''}`.trim()}
      to={path}
    >
      <span className='nav-icon'>{icon}</span>
      {children}
      <FullRightTriangle />
    </MobileNavigationBoxLink>
  );
};

// Sidebar link on the light sidebar (Vercel/Mercury): quiet label, soft
// hover, active item a white chip with a hairline and a lime marker.
// `active-grey` is the settings variant, where the active item is an ink pill.
export const NavigationBoxLink = styled(RouterLink)`
  position: relative;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 10px;
  padding: 0 10px;
  height: 34px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 500;
  color: var(--muted);
  white-space: nowrap;
  border: 1px solid transparent;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  & .nav-icon {
    display: inline-flex;
    flex: none;
    width: 18px;
    justify-content: center;
    font-size: 15px;
    color: inherit;
  }

  & .nav-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & .nav-badge {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--ink);
    color: #ffffff;
    font-size: 11.5px;
    font-weight: 600;
    line-height: 20px;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  &:hover {
    color: var(--ink);
    background-color: var(--hover);
  }

  &.active {
    color: var(--ink);
    background-color: #ffffff;
    border-color: var(--line);
    box-shadow: var(--shadow-xs);
    font-weight: 600;

    &::before {
      content: '';
      position: absolute;
      left: -11px;
      top: 8px;
      bottom: 8px;
      width: 3px;
      border-radius: 0 3px 3px 0;
      background: var(--ok);
    }
  }

  &.active-grey {
    color: var(--ink);

    &:hover {
      background-color: var(--ground);
    }

    &.active {
      color: #ffffff;
      background-color: var(--ink);
    }
  }
`;

export const InternalNavLink = styled(RouterLink)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.navLinkColor};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.navLinkColor};

  &:hover {
    color: #0E1A13a7 !important;
  }

  &.footer {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    font-weight: 400;
  }

  &.hamburger {
    color: ${({ theme }) => theme.palette.navLinkColor};
    font-size: 1rem;
    font-weight: 600;
  }
`;

const MobileNavigationBoxLink = styled(RouterLink)`
  text-decoration: none;
  color: var(--ink);
  display: grid;
  grid-template-columns: 16px 1fr 16px;
  gap: 14px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 18px 20px;
  background-color: white;
  align-items: center;
  font-weight: 500;

  & .nav-icon {
    display: inline-flex;
    color: inherit;
  }

  &.active {
    color: #ffffff;
    background-color: var(--ink);
    border-color: var(--ink);
  }
`;
